// frontend/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import MainLayout from './components/Layout/MainLayout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ContentLibraryPage from './pages/ContentLibraryPage';
import ContentViewPage from './pages/ContentViewPage';
import ArchivePage from './pages/ArchivePage';
import TopicsPage from './pages/TopicsPage';
import SettingsPage from './pages/SettingsPage';
import BrandVoicePage from './pages/BrandVoicePage';
import UsersPage from './pages/UsersPage';
import SourcesPage from './pages/SourcesPage';
import ApiKeysPage from './pages/ApiKeysPage';
import { Loader } from 'lucide-react';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !isAdmin()) return <Navigate to="/" />;

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPasswordPage />} />

      <Route path="/" element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="content" element={<ContentLibraryPage />} />
        <Route path="content/:id" element={<ContentViewPage />} />
        <Route path="archive" element={<ArchivePage />} />
        <Route path="sources" element={<SourcesPage />} />
        <Route path="topics" element={
          <ProtectedRoute adminOnly><TopicsPage /></ProtectedRoute>
        } />
        <Route path="brand-voice" element={
          <ProtectedRoute adminOnly><BrandVoicePage /></ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>
        } />
        <Route path="api-keys" element={
          <ProtectedRoute adminOnly><ApiKeysPage /></ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute adminOnly><UsersPage /></ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
